import {CompilerService} from '../compiler/CompilerService';
import {CSSLanguageDefaults, HTMLLanguageDefaults, WorkerStorage, Project, TypeScriptWorkerWrapper} from '../../types';
import type {CSSWorker, HTMLWorker} from '../../types';
import {languageConfigurations} from './languageConfigurations';
import * as Monaco from 'monaco-editor';

const MARKER_OWNERS = {
    MOTOKO: 'motoko-diagnostics',
    TYPESCRIPT: 'typescript-diagnostics',
    JAVASCRIPT: 'javascript-diagnostics',
    CSS: 'css-diagnostics',
    HTML: 'html-diagnostics',
    GENERAL: 'diagnostics'
};

export class LanguageService {
    private monaco: typeof Monaco;
    private editor: Monaco.editor.IStandaloneCodeEditor;
    private compilerService: CompilerService;
    private disposed = false;
    private project: Project;
    private files: { name: string; content: string }[]; // Add this line
    private registeredFiles: Set<string> = new Set(); // Add this line
    private currentDiagnosticCode: number = 0;

// Complete LanguageService constructor in languageService.ts
    constructor(
        monaco: typeof Monaco,
        editor: Monaco.editor.IStandaloneCodeEditor,
        compilerService: CompilerService,
        project: Project,
        files: { name: string; content: string }[]
    ) {
        this.monaco = monaco;
        this.editor = editor;
        this.compilerService = compilerService;
        this.project = project;

        // Filter files to only include current project files
        const projectName = project.name;
        const filteredFiles = files.filter(file => {
            // Check if file belongs to current project
            const normalizedPath = this.normalizePath(file.name);
            const fileProjectMatch = normalizedPath.match(/^([^\/]+)\//);
            const fileProjectName = fileProjectMatch ? fileProjectMatch[1] : null;

            // Include files that belong to the same project or have no project prefix
            return !fileProjectName || fileProjectName === projectName ||
                // Also include files that start with the project name
                file.name.startsWith(projectName + '/') ||
                // Include files without a clear project structure (root level files)
                !file.name.includes('/') ||
                // Include files that are directly in src/ without a project prefix
                file.name.startsWith('src/');
        });

        console.log(`[LanguageService] Filtered to ${filteredFiles.length}/${files.length} files for project "${projectName}"`);
        console.log(`[LanguageService] Project files: ${filteredFiles.map(f => f.name).slice(0, 5).join(', ')}${filteredFiles.length > 5 ? '...' : ''}`);

        this.files = filteredFiles;
        this.registeredFiles = new Set();
        this.currentDiagnosticCode = 0;
        this.initializeLanguages();
    }

    private normalizePath(path: string): string {
        let normalized = path.replace(/\/+/g, '/');
        if (normalized.endsWith('/') && normalized.length > 1) {
            normalized = normalized.slice(0, -1);
        }

        // Remove project ID prefixes for consistent comparison
        const projectPrefixMatch = normalized.match(/^(project-[^\/]+)\//);
        if (projectPrefixMatch) {
            normalized = normalized.substring(projectPrefixMatch[0].length);
        }

        if (normalized.includes(':')) {
            const colonParts = normalized.split(':');
            normalized = colonParts[colonParts.length - 1];
        }

        return normalized;
    }

    private diagnosticsTimeout: NodeJS.Timeout | null = null;

    public initializeLanguages() {
        if (!this.monaco.languages) return;

        Object.entries(languageConfigurations).forEach(([langKey, config]) => {
            // Register the language
            this.monaco.languages.register({
                id: config.id,
                extensions: config.extensions
            });

            // Set language configuration
            this.monaco.languages.setLanguageConfiguration(config.id, config.config);
            this.monaco.languages.setMonarchTokensProvider(config.id, config.monarchTokens);

            // Setup language-specific features
            this.setupLanguageFeatures(config.id);
        });
    }

    public setLanguageForModel(model: Monaco.editor.ITextModel, language: string) {
        this.monaco.editor.setModelLanguage(model, language);
    }

    private setupLanguageFeatures(languageId: string) {
        switch (languageId) {
            case 'typescript':
            case 'javascript':
                this.setupTypeScriptFeatures(languageId);
                break;
            case 'css':
                this.setupCSSFeatures();
                break;
            case 'html':
                this.setupHTMLFeatures();
                break;
            case 'motoko':
                this.setupMotokoFeatures();
                break;
        }
    }

    private workers: WorkerStorage = {};

    private async getWorker(type: 'typescript' | 'javascript' | 'css' | 'html'): Promise<TypeScriptWorkerWrapper | CSSWorker | HTMLWorker | null> {
        // First check if the worker type is already cached
        if (this.workers[type]) return this.workers[type];

        try {
            const model = this.editor.getModel();
            if (!model) return null;

            // For TypeScript and JavaScript, add explicit checks
            if (type === 'typescript' || type === 'javascript') {
                // Check if TypeScript is available in Monaco
                if (!this.monaco.languages.typescript) {
                    console.error(`TypeScript language service not available`);
                    return null;
                }

                // Check if the getTypeScriptWorker method exists
                if (!this.monaco.languages.typescript.getTypeScriptWorker) {
                    console.error('TypeScript worker getter not available yet');
                    return null;
                }
            }

            let worker: TypeScriptWorkerWrapper | CSSWorker | HTMLWorker | null = null;

            switch (type) {
                case 'html': {
                    // HTML worker initialization code...
                    break;
                }
                case 'css': {
                    // CSS worker initialization code...
                    break;
                }
                case 'typescript':
                case 'javascript': {
                    try {
                        // Create a typed worker that's directly usable without calling as a function
                        const workerGetter = await this.monaco.languages.typescript.getTypeScriptWorker();
                        const tsWorker = await workerGetter(model.uri);

                        // Create our wrapper with both callable and direct method access
                        const typedWorker: TypeScriptWorkerWrapper = Object.assign(
                            // Make it callable to return a client
                            async (uri: Monaco.Uri) => {
                                return {
                                    getSyntacticDiagnostics: (uriString: string) =>
                                        tsWorker.getSyntacticDiagnostics(uriString),
                                    getSemanticDiagnostics: (uriString: string) =>
                                        tsWorker.getSemanticDiagnostics(uriString)
                                };
                            },
                            // Add direct methods
                            {
                                getSyntacticDiagnostics: (uri: string) => tsWorker.getSyntacticDiagnostics(uri),
                                getSemanticDiagnostics: (uri: string) => tsWorker.getSemanticDiagnostics(uri)
                            }
                        );

                        worker = typedWorker;

                        if (type === 'typescript') {
                            this.workers.typescript = typedWorker;
                        } else {
                            this.workers.javascript = typedWorker;
                        }

                        console.log(`[DEBUG] Created ${type} worker successfully`);
                    } catch (tsError) {
                        console.error(`Error getting TypeScript worker:`, tsError);
                        return null;
                    }
                    break;
                }
            }

            return worker;
        } catch (error) {
            console.error(`Failed to initialize ${type} worker:`, error);
            return null;
        }
    }

    private ensureTypeScriptWorker(): Promise<void> {
        return new Promise((resolve, reject) => {
            // Try up to 5 times with increasing delays
            let attempts = 0;
            const maxAttempts = 5;

            const tryGetWorker = () => {
                attempts++;

                if (!this.monaco.languages.typescript) {
                    if (attempts < maxAttempts) {
                        console.log(`[DEBUG] TypeScript not available, attempt ${attempts}/${maxAttempts}, retrying...`);
                        setTimeout(tryGetWorker, 500 * attempts); // Increasing delay
                    } else {
                        reject(new Error("TypeScript language service not available after multiple attempts"));
                    }
                    return;
                }

                if (!this.monaco.languages.typescript.getTypeScriptWorker) {
                    if (attempts < maxAttempts) {
                        console.log(`[DEBUG] TypeScript worker getter not available, attempt ${attempts}/${maxAttempts}, retrying...`);
                        setTimeout(tryGetWorker, 500 * attempts);
                    } else {
                        reject(new Error("TypeScript worker getter not available after multiple attempts"));
                    }
                    return;
                }

                // TypeScript is now available
                console.log(`[DEBUG] TypeScript worker available after ${attempts} attempts`);
                resolve();
            };

            // Start the first attempt
            tryGetWorker();
        });
    }

    private setupCSSFeatures() {
        if (this.monaco.languages.css?.cssDefaults) {
            const defaults = this.monaco.languages.css.cssDefaults as CSSLanguageDefaults;
            const cssOptions: Monaco.languages.css.Options = {
                validate: true,
                lint: {
                    compatibleVendorPrefixes: 'warning',
                    vendorPrefix: 'warning',
                    duplicateProperties: 'warning',
                    emptyRules: 'warning',
                    importStatement: 'warning',
                    boxModel: 'warning',
                    universalSelector: 'warning',
                    zeroUnits: 'warning',
                    fontFaceProperties: 'warning',
                    hexColorLength: 'warning',
                    argumentsInColorFunction: 'warning',
                    unknownProperties: 'warning',
                    ieHack: 'ignore',
                    unknownVendorSpecificProperties: 'warning',
                    propertyIgnoredDueToDisplay: 'warning',
                    important: 'warning',
                    float: 'warning'
                }
            };

            defaults.setOptions(cssOptions);
        }
    }

    private setupHTMLFeatures() {
        if (this.monaco.languages.html?.htmlDefaults) {
            const defaults = this.monaco.languages.html.htmlDefaults as HTMLLanguageDefaults;
            const htmlOptions: Monaco.languages.html.Options = {
                format: {
                    tabSize: 2,
                    insertSpaces: true,
                    wrapLineLength: 120,
                    unformatted: '',
                    contentUnformatted: 'pre,code,textarea',
                    indentInnerHtml: false,
                    preserveNewLines: true,
                    maxPreserveNewLines: null,
                    indentHandlebars: false,
                    endWithNewline: false,
                    extraLiners: 'head, body, /html',
                    wrapAttributes: 'auto'
                },
                suggest: {html5: true}
            };

            defaults.setOptions(htmlOptions);
        }
    }

    private setupMotokoFeatures() {
        this.editor.updateOptions({
            parameterHints: {enabled: false}
        });

        this.monaco.languages.registerSignatureHelpProvider('motoko', {
            signatureHelpTriggerCharacters: [],
            signatureHelpRetriggerCharacters: [],
            provideSignatureHelp: () => null
        });
    }

    public async handleDiagnostics(fileName: string, content: string) {
        if (this.disposed) return;

        const model = this.editor.getModel();
        if (!model) return;

        // Get the call stack to determine the source
        const stack = new Error().stack || '';

        // Check if this is called during file switching
        // If it's coming from setActiveFile, we should abort to prevent unwanted file switching
        if (stack.includes('setActiveFile')) {
            console.log('[DEBUG] Aborting diagnostics run triggered by file switching');
            return;
        }

        // IMPORTANT: Skip diagnostics for specific file types to prevent file switching
        const skipExtensions = ['.did', '.json', '.md', '.txt', '.dylib', '.wasm'];
        if (skipExtensions.some(ext => fileName.endsWith(ext))) {
            console.log(`[DEBUG] Skipping diagnostics for file type: ${fileName}`);
            return;
        }

        try {
            const extension = fileName.split('.').pop()?.toLowerCase();
            switch (extension) {
                case 'mo':
                    await this.handleMotokoDiagnostics(fileName, content, model);
                    break;
                case 'ts':
                case 'tsx':
                    await this.handleTypeScriptDiagnostics(model);
                    break;
                case 'js':
                case 'jsx':
                    await this.handleJavaScriptDiagnostics(model);
                    break;
                case 'css':
                    await this.handleCSSDiagnostics(model);
                    break;
                case 'html':
                    await this.handleHTMLDiagnostics(model);
                    break;
            }
        } catch (error) {
            console.error('Error in handleDiagnostics:', error);
            this.setErrorMarkers(model, [{
                severity: this.monaco.MarkerSeverity.Error,
                message: String(error),
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: 1,
                endColumn: 1
            }]);
        }
    }

    private async handleMotokoDiagnostics(fileName: string, content: string, model: Monaco.editor.ITextModel) {
        console.log('[DEBUG] Starting Motoko diagnostics with file:', fileName);

        // Always clean all markers first to ensure a clean slate
        this.forceCleanAllMarkers(model);

        if (!this.compilerService) {
            console.error('[ERROR] Compiler service not available');
            return;
        }

        try {
            // Clear previous markers specifically
            this.clearMarkers(model, MARKER_OWNERS.MOTOKO);

            // Get installed packages for filtering
            const installedPackages = new Set<string>();
            if (this.project?.motokoPackages &&
                Array.isArray(this.project.motokoPackages) &&
                this.project.motokoPackages.length > 0 &&
                Array.isArray(this.project.motokoPackages[0])) {
                this.project.motokoPackages[0].forEach(pkg => {
                    if (pkg && pkg.name) {
                        installedPackages.add(pkg.name);
                    }
                });
            }

            console.log('[DEBUG] Project name for diagnostics:', this.project?.name);
            console.log('[DEBUG] Installed packages:', Array.from(installedPackages));

            // Save the current file content
            await this.compilerService.mocCommand({
                type: 'save',
                file: fileName,
                content: content
            });

            // Run the check command
            const result = await this.compilerService.mocCommand({
                type: 'check',
                file: fileName
            });

            console.log('[DEBUG] Compiler check result:', result);

            let markers: Monaco.editor.IMarkerData[] = [];

            if (result?.diagnostics?.diagnostics) {
                const diagnostics = Array.isArray(result.diagnostics.diagnostics)
                    ? result.diagnostics.diagnostics
                    : [result.diagnostics.diagnostics];

                console.log('[DEBUG] Raw compiler diagnostics:', diagnostics);

                // Filter diagnostics:
                // 1. Remove phantom Types/lib.mo errors
                // 2. Remove package not defined errors for packages we know are installed
                // 3. Remove lib.mo errors for modules that exist directly as .mo files
                const filteredDiagnostics = diagnostics.filter(d => {
                    // Filter out phantom Types/lib.mo errors
                    if (d.message &&
                        d.message.includes('/Types/lib.mo') &&
                        d.message.includes('does not exist')) {
                        console.log('[DEBUG] Filtering out Types/lib.mo error:', d.message);
                        return false;
                    }

                    // Filter out "package X not defined" errors for installed packages
                    if (d.code === 'M0010' && d.message) {
                        // Extract the package name from the error message
                        const packageMatch = d.message.match(/package "([^"]+)" not defined/);
                        if (packageMatch && packageMatch[1]) {
                            const packageName = packageMatch[1];
                            if (installedPackages.has(packageName)) {
                                console.log('[DEBUG] Filtering out error for installed package:', packageName);
                                return false;
                            }
                        }
                    }

                    // Filter out lib.mo errors but only if the corresponding .mo file exists
                    if (d.message && d.message.includes('lib.mo') && d.message.includes('does not exist')) {
                        // Extract the path without lib.mo
                        const pathMatch = d.message.match(/file "([^"]+)\/lib\.mo"/);
                        if (pathMatch && pathMatch[1]) {
                            const directoryPath = pathMatch[1]; // e.g., "backend/types"
                            const moduleFileName = directoryPath.split('/').pop() + '.mo'; // e.g., "types.mo"
                            const modulePath = directoryPath.substring(0, directoryPath.lastIndexOf('/')) + '/' + moduleFileName;

                            // Check if the module file (e.g., backend/types.mo) exists in our files list
                            const moduleExists = this.files.some(file =>
                                file.name === modulePath ||
                                file.name.endsWith('/' + moduleFileName)
                            );

                            if (moduleExists) {
                                console.log('[DEBUG] Filtering out lib.mo error for existing module:', modulePath);
                                return false;
                            }
                        }
                    }

                    return true;
                });

                console.log('[DEBUG] Filtered diagnostics:', filteredDiagnostics);

                markers = filteredDiagnostics.map(d => ({
                    severity: this.translateSeverity(d.severity),
                    startLineNumber: (d.range?.start?.line ?? 0) + 1,
                    startColumn: (d.range?.start?.character ?? 0) + 1,
                    endLineNumber: (d.range?.end?.line ?? d.range?.start?.line ?? 0) + 1,
                    endColumn: (d.range?.end?.character ?? d.range?.start?.character ?? 0) + 1,
                    message: d.message || 'Unknown error',
                    code: d.code || '',
                    source: 'motoko'
                }));
            }

            // Add custom import validations for non-package imports
            const importMarkers = await this.validateMotokoImports(content);
            console.log('[DEBUG] Import markers found:', importMarkers.length);
            markers = [...markers, ...importMarkers];

            // Add detailed logging for markers
            console.log(`[DEBUG] Final markers count before setting: ${markers.length}`);
            if (markers.length > 0) {
                console.log('[DEBUG] Marker details:');
                markers.forEach((marker, index) => {
                    console.log(`[${index}] Line ${marker.startLineNumber}:${marker.startColumn} - ${marker.message.substring(0, 100)}${marker.message.length > 100 ? '...' : ''}`);
                });
            }

            // Set markers with the specific owner
            console.log(`[DEBUG] Setting ${markers.length} markers on model via owner: "${MARKER_OWNERS.MOTOKO}"`);
            this.monaco.editor.setModelMarkers(model, MARKER_OWNERS.MOTOKO, markers);

            // Verify markers were set by checking current markers
            setTimeout(() => {
                try {
                    const currentMarkers = this.monaco.editor.getModelMarkers({resource: model.uri});
                    console.log(`[DEBUG] Current markers after setting: ${currentMarkers.length} (${currentMarkers.filter(m => m.owner === MARKER_OWNERS.MOTOKO).length} with owner ${MARKER_OWNERS.MOTOKO})`);

                    if (markers.length > 0 && currentMarkers.length === 0) {
                        console.warn('[WARNING] Markers appear to have been intercepted or filtered out by another component!');
                    }
                } catch (e) {
                    console.error('[ERROR] Failed to verify markers:', e);
                }
            }, 50);

        } catch (error) {
            console.error('[ERROR] Error in handleMotokoDiagnostics:', error);
            this.setErrorMarkers(model, [{
                severity: this.monaco.MarkerSeverity.Error,
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: 1,
                endColumn: 1,
                message: `Diagnostic error: ${error instanceof Error ? error.message : String(error)}`,
                source: 'motoko'
            }]);
        }
    }

// Complete validateMotokoImports method - replace the existing one entirely
    private async validateMotokoImports(content: string): Promise<Monaco.editor.IMarkerData[]> {
        const markers: Monaco.editor.IMarkerData[] = [];
        const importRegex = /import\s+(\w+)\s+"([^"]+)"/g;
        let match;

        const self = this;

        console.log("[DEBUG] Starting Motoko import validation");

        try {
            // 1. Get current project context FIRST
            const currentModel = this.editor.getModel();
            if (!currentModel) {
                console.error("[ERROR] Unable to get current model for import validation");
                return markers;
            }

            // Get the current file path and normalize it
            const currentFilePath = this.normalizePath(currentModel.uri.path.startsWith('/')
                ? currentModel.uri.path.substring(1)
                : currentModel.uri.path);

            // Extract the project name from the current file path
            const currentProjectMatch = currentFilePath.match(/^([^\/]+)\//);
            const currentProjectName = currentProjectMatch ? currentProjectMatch[1] : this.project.name;

            console.log(`[DEBUG] Current file: ${currentFilePath}`);
            console.log(`[DEBUG] Detected current project: ${currentProjectName}`);

            // 2. Get installed packages for filtering
            const installedPackages = new Set<string>();
            if (this.project?.motokoPackages &&
                Array.isArray(this.project.motokoPackages) &&
                this.project.motokoPackages.length > 0 &&
                Array.isArray(this.project.motokoPackages[0])) {
                this.project.motokoPackages[0].forEach(pkg => {
                    if (pkg && pkg.name) {
                        installedPackages.add(pkg.name);
                    }
                });
            }

            // 3. Built-in packages that don't need validation
            const builtInPackages = new Set(['prim', 'mo:prim', 'mo:base', 'mo:⛔', 'base']);

            // 4. Project-aware file filtering (files are already filtered in constructor)
            // Create lookup structures ONLY for current project files
            const filesByPath = new Map<string, { name: string, content: string }>();
            const filesByName = new Map<string, Set<string>>();
            const dirContents = new Map<string, Set<string>>();

            // Process the already-filtered files
            this.files.forEach(file => {
                if (!file.name.endsWith('.mo')) return;

                const normalizedPath = this.normalizePath(file.name);
                
                // Add multiple path variations to ensure we can find files
                filesByPath.set(file.name, file);
                filesByPath.set(normalizedPath, file);
                
                // Also add without .mo extension
                const pathWithoutExt = normalizedPath.replace(/\.mo$/, '');
                filesByPath.set(pathWithoutExt, file);
                
                // Add original path without extension
                const originalPathWithoutExt = file.name.replace(/\.mo$/, '');
                filesByPath.set(originalPathWithoutExt, file);

                const pathParts = normalizedPath.split('/');
                const fileName = pathParts.pop() || '';
                const fileNameNoExt = fileName.replace(/\.mo$/, '');
                const dirPath = pathParts.join('/');

                // Map filenames to paths (case-insensitive and case-sensitive)
                const lowerFileName = fileNameNoExt.toLowerCase();
                if (!filesByName.has(lowerFileName)) {
                    filesByName.set(lowerFileName, new Set());
                }
                filesByName.get(lowerFileName)?.add(normalizedPath);
                filesByName.get(lowerFileName)?.add(file.name);

                if (!filesByName.has(fileNameNoExt)) {
                    filesByName.set(fileNameNoExt, new Set());
                }
                filesByName.get(fileNameNoExt)?.add(normalizedPath);
                filesByName.get(fileNameNoExt)?.add(file.name);

                // Map directories to their contents (multiple directory path variations)
                const dirVariations = [
                    dirPath,
                    normalizedPath.substring(0, normalizedPath.lastIndexOf('/')),
                    file.name.substring(0, file.name.lastIndexOf('/')),
                    this.normalizePath(file.name.substring(0, file.name.lastIndexOf('/')))
                ].filter(Boolean);

                dirVariations.forEach(dir => {
                    if (!dirContents.has(dir)) {
                        dirContents.set(dir, new Set());
                    }
                    dirContents.get(dir)?.add(fileNameNoExt);
                    dirContents.get(dir)?.add(lowerFileName); // Also add lowercase for case-insensitive matching
                });
            });

            console.log(`[DEBUG] Built lookup structures from ${this.files.length} project files`);

            // 5. Get current directory
            const currentFilePathParts = currentFilePath.split('/');
            currentFilePathParts.pop(); // Remove filename
            const currentDirPath = currentFilePathParts.join('/');

            // 6. Process all imports
            while ((match = importRegex.exec(content)) !== null) {
                const [fullMatch, identifier, importPath] = match;
                console.log(`[DEBUG] Processing import: ${identifier} from "${importPath}"`);

                // Skip built-in packages
                if (builtInPackages.has(importPath)) {
                    console.log(`[DEBUG] Skipping built-in package: ${importPath}`);
                    continue;
                }

                // Handle 'mo:' package imports
                if (importPath.startsWith('mo:')) {
                    const packagePathParts = importPath.substring(3).split('/');
                    const packageName = packagePathParts[0];

                    if (!installedPackages.has(packageName) && !builtInPackages.has(`mo:${packageName}`)) {
                        addMarker(fullMatch, match.index,
                            `Package "${packageName}" is not installed. Add it to your project's Motoko packages.`);
                    } else {
                        console.log(`[DEBUG] Valid package import: ${packageName}`);
                    }
                    continue;
                }

                // Check if import exists in current project
                const importFound = checkImportExists(
                    importPath,
                    filesByPath,
                    filesByName,
                    dirContents,
                    currentDirPath,
                    currentProjectName
                );

                if (!importFound) {
                    console.log(`[DEBUG] Import NOT found in current project - adding error marker: ${importPath}`);
                    addMarker(fullMatch, match.index,
                        `Module "${importPath}" could not be found in project "${currentProjectName}". Check that the file exists and the path is correct.`);
                } else {
                    console.log(`[DEBUG] Import found successfully - no error marker needed: ${importPath}`);
                }
            }

            console.log(`[DEBUG] Final markers count: ${markers.length}`);

        } catch (error) {
            console.error('[ERROR] Error in validateMotokoImports:', error);
        }

        return markers;

        /**
         * Check if import exists in current project
         */
        function checkImportExists(
            originalImport: string,
            filesByPath: Map<string, { name: string, content: string }>,
            filesByName: Map<string, Set<string>>,
            dirContents: Map<string, Set<string>>,
            currentDir: string,
            currentProjectName: string | null
        ): boolean {
            console.log(`[DEBUG] Checking existence of import: "${originalImport}" in project: ${currentProjectName}`);
            console.log(`[DEBUG] Current directory: "${currentDir}"`);

            // Handle relative imports (./file or ../file)
            let moduleName = originalImport.replace(/\.mo$/, '');
            let resolvedImport = originalImport;
            
            // Handle relative paths
            if (originalImport.startsWith('./')) {
                resolvedImport = originalImport.substring(2);
                moduleName = resolvedImport.replace(/\.mo$/, '');
            } else if (originalImport.startsWith('../')) {
                // For ../ imports, go up one directory
                const parentDir = currentDir.substring(0, currentDir.lastIndexOf('/'));
                resolvedImport = originalImport.substring(3);
                moduleName = resolvedImport.replace(/\.mo$/, '');
            }
            
            const moduleNameLower = moduleName.toLowerCase();

            // Strategy 1: Project-specific path resolution
            const pathResolutionStrategies = [
                // Current directory relative paths (highest priority for same-directory imports)
                `${currentDir}/${moduleName}`,
                `${currentDir}/${moduleName}.mo`,
                `${currentDir}/${resolvedImport}`,
                `${currentDir}/${resolvedImport}.mo`,

                // Project-specific absolute paths
                currentProjectName ? `${currentProjectName}/src/backend/${moduleName}` : '',
                currentProjectName ? `${currentProjectName}/src/backend/${moduleName}.mo` : '',
                currentProjectName ? `${currentProjectName}/src/${moduleName}` : '',
                currentProjectName ? `${currentProjectName}/src/${moduleName}.mo` : '',
                currentProjectName ? `${currentProjectName}/${moduleName}` : '',
                currentProjectName ? `${currentProjectName}/${moduleName}.mo` : '',

                // Fallback paths without project prefix
                `src/backend/${moduleName}`,
                `src/backend/${moduleName}.mo`,
                `backend/${moduleName}`,
                `backend/${moduleName}.mo`,
                `src/${moduleName}`,
                `src/${moduleName}.mo`,

                // Direct reference
                moduleName,
                `${moduleName}.mo`
            ].filter(Boolean);

            console.log(`[DEBUG] Trying path resolution strategies:`);

            for (const path of pathResolutionStrategies) {
                const normalizedPath = self.normalizePath(path);
                // Try multiple path variations
                const pathVariations = [
                    path,
                    normalizedPath,
                    path.replace(/\.mo$/, ''),
                    normalizedPath.replace(/\.mo$/, '')
                ];
                
                for (const pathVar of pathVariations) {
                    const exists = filesByPath.has(pathVar);
                    if (exists) {
                        console.log(`  - ${path} (variation: ${pathVar}): FOUND`);
                        return true;
                    }
                }
                console.log(`  - ${path} (normalized: ${normalizedPath}): NOT FOUND`);
            }

            // Strategy 2: Check by name within current project only
            if (filesByName.has(moduleName) || filesByName.has(moduleNameLower)) {
                const modulePaths = filesByName.get(moduleName) || filesByName.get(moduleNameLower) || new Set();
                console.log(`[DEBUG] Found module by name in current project: ${Array.from(modulePaths).join(', ')}`);
                return true;
            }

            // Strategy 3: Check in current directory (try multiple directory path variations)
            const currentDirVariations = [
                currentDir,
                self.normalizePath(currentDir),
                currentDir.replace(/\/$/, ''), // Remove trailing slash
                self.normalizePath(currentDir.replace(/\/$/, ''))
            ];
            
            for (const dirVar of currentDirVariations) {
                if (dirContents.has(dirVar)) {
                    const dirFiles = dirContents.get(dirVar) || new Set();
                    // Check both case-sensitive and case-insensitive
                    if (dirFiles.has(moduleName) || dirFiles.has(moduleNameLower)) {
                        console.log(`[DEBUG] Found module in current directory: ${dirVar}/${moduleName}`);
                        return true;
                    }
                }
            }

            // Strategy 4: Check common project locations
            const commonDirectories = [
                currentProjectName ? `${currentProjectName}/src/backend` : '',
                currentProjectName ? `${currentProjectName}/src` : '',
                currentProjectName ? `${currentProjectName}/lib` : '',
                currentProjectName ? `${currentProjectName}/modules` : '',
                'src/backend',
                'src',
                'backend',
                'lib',
                'modules'
            ].filter(Boolean);

            for (const dir of commonDirectories) {
                const normalizedDir = self.normalizePath(dir);
                // Try multiple directory path variations
                const dirVariations = [dir, normalizedDir, dir.replace(/\/$/, ''), normalizedDir.replace(/\/$/, '')];
                
                for (const dirVar of dirVariations) {
                    if (dirContents.has(dirVar)) {
                        const dirFiles = dirContents.get(dirVar) || new Set();
                        // Check both case-sensitive and case-insensitive
                        if (dirFiles.has(moduleName) || dirFiles.has(moduleNameLower)) {
                            console.log(`[DEBUG] Found module in common location: ${dirVar}/${moduleName}`);
                            return true;
                        }
                    }
                }
            }

            console.log(`[DEBUG] Module not found in current project: ${originalImport}`);
            return false;
        }

        /**
         * Add a diagnostic marker for an invalid import
         */
        function addMarker(fullMatch: string, matchIndex: number, message: string): void {
            const startPos = getPositionAt(content, matchIndex);
            const endPos = getPositionAt(content, matchIndex + fullMatch.length);

            markers.push({
                severity: self.monaco.MarkerSeverity.Error,
                startLineNumber: startPos.line,
                startColumn: startPos.column,
                endLineNumber: endPos.line,
                endColumn: endPos.column,
                message: message,
                source: 'motoko-import-validation',
            });

            console.log(`[DEBUG] Added marker: ${message}`);
        }

        /**
         * Calculate line and column position from character offset
         */
        function getPositionAt(text: string, offset: number): { line: number; column: number } {
            if (!text || offset <= 0) return {line: 1, column: 1};
            if (offset > text.length) offset = text.length;

            const lines = text.slice(0, offset).split('\n');
            const line = lines.length;
            const column = lines[lines.length - 1].length + 1;

            return {line, column};
        }
    }

    private setupTypeScriptFeatures(languageId: string): void {
        // Verify TypeScript is available
        if (!this.monaco.languages.typescript) {
            console.error('TypeScript language service not available');
            return;
        }

        const typescript = this.monaco.languages.typescript;
        const defaults = languageId === 'typescript'
            ? typescript.typescriptDefaults
            : typescript.javascriptDefaults;

        console.log(`[DEBUG] Setting up ${languageId} features`);

        // First, build a map of all available files
        const projectFiles = new Map<string, { name: string; content: string }>();
        const moduleMap = new Map<string, string[]>();

        // Track files by basename (without extension) for import resolution
        this.files.forEach(file => {
            projectFiles.set(file.name, file);

            // Extract basename for module mapping
            const basename = file.name.split('/').pop() || '';
            const moduleName = basename.replace(/\.(ts|tsx|js|jsx)$/, '');

            if (!moduleMap.has(moduleName)) {
                moduleMap.set(moduleName, []);
            }
            moduleMap.get(moduleName)?.push(file.name);

            // Also add directory information
            const dirPath = file.name.substring(0, file.name.lastIndexOf('/'));
            const dirName = dirPath.split('/').pop() || '';
            if (dirName && !moduleMap.has(dirName)) {
                moduleMap.set(dirName, []);
            }
            moduleMap.get(dirName)?.push(dirPath);
        });

        // Create path mappings dynamically based on project structure
        // This helps TypeScript resolve imports across the project
        const pathMappings: { [key: string]: string[] } = { "*": ["*"] };

        // Add each module to paths for flexible import resolution
        moduleMap.forEach((paths, moduleName) => {
            pathMappings[`./${moduleName}`] = paths.map(p => p.endsWith('.ts') || p.endsWith('.tsx') ? p : `${p}/*`);
            pathMappings[`../${moduleName}`] = paths.map(p => p.endsWith('.ts') || p.endsWith('.tsx') ? p : `${p}/*`);
        });

        // Log the dynamic path mappings
        console.log('[DEBUG] Dynamic TypeScript path mappings:', pathMappings);

        // Set comprehensive compiler options
        const compilerOptions = {
            target: typescript.ScriptTarget.ESNext,
            moduleResolution: typescript.ModuleResolutionKind.NodeJs,
            module: typescript.ModuleKind.ESNext,
            jsx: typescript.JsxEmit.React,
            jsxFactory: 'React.createElement',
            jsxFragmentFactory: 'React.Fragment',
            allowSyntheticDefaultImports: true,
            esModuleInterop: true,
            baseUrl: ".",
            paths: pathMappings,
            skipLibCheck: true,
            allowNonTsExtensions: true,
            resolveJsonModule: true,
            noImplicitAny: false,
            strict: false,
            allowJs: true,
            checkJs: false
        };

        // Configure diagnostics to be more lenient with imports
        const diagnosticsOptions = {
            noSemanticValidation: false,
            noSyntaxValidation: false,
            noSuggestionDiagnostics: false,
            diagnosticCodesToIgnore: [2307], // Ignore "Cannot find module" errors completely
            reportMissingImports: {severity: "warning"}, // Report missing imports as warnings instead of errors
            reportUnusedLabels: {severity: "warning"}
        };

        // Apply configurations
        defaults.setCompilerOptions(compilerOptions);
        defaults.setDiagnosticsOptions(diagnosticsOptions);

        // Register all project files with TypeScript service
        console.log('[DEBUG] Registering TypeScript project files:');
        const registeredCount = {total: 0, ts: 0, tsx: 0, js: 0, jsx: 0};

        // CRITICAL: Normalize file paths to match bundler expectations
        // The bundler normalizes paths in normalizeFilesForProjectType, so Monaco should use the same paths
        // This ensures import resolution matches what the bundler will see
        const normalizePathForMonaco = (filePath: string): string => {
            let normalized = filePath;
            
            // Remove project name prefix if present (bundler does this too)
            const projectName = this.project.name;
            if (projectName && normalized.startsWith(projectName + '/')) {
                normalized = normalized.substring(projectName.length + 1);
            }
            
            // Remove project ID prefix if present
            const projectIdMatch = normalized.match(/^(project-[^\/]+)\//);
            if (projectIdMatch) {
                normalized = normalized.substring(projectIdMatch[0].length);
            }
            
            // Ensure forward slashes and remove leading slashes
            normalized = normalized.replace(/\\/g, '/').replace(/^\/+/, '');
            
            return normalized;
        };

        // Register files with both TypeScript and JavaScript services
        const registerWithServices = (filePath: string, content: string) => {
            // CRITICAL: Use normalized path to match bundler expectations
            const normalizedPath = normalizePathForMonaco(filePath);
            const virtualFilePath = `file:///${normalizedPath}`;
            
            typescript.typescriptDefaults.addExtraLib(content, virtualFilePath);
            typescript.javascriptDefaults.addExtraLib(content, virtualFilePath);
            this.registeredFiles.add(virtualFilePath);
            
            // Also register with original path for backwards compatibility
            const originalVirtualPath = `file:///${filePath}`;
            if (!this.registeredFiles.has(originalVirtualPath)) {
                typescript.typescriptDefaults.addExtraLib(content, originalVirtualPath);
                typescript.javascriptDefaults.addExtraLib(content, originalVirtualPath);
                this.registeredFiles.add(originalVirtualPath);
            }
        };

        // Register all project files
        this.files.forEach(file => {
            let ext = '';
            if (file.name.endsWith('.ts')) ext = 'ts';
            else if (file.name.endsWith('.tsx')) ext = 'tsx';
            else if (file.name.endsWith('.js')) ext = 'js';
            else if (file.name.endsWith('.jsx')) ext = 'jsx';
            else return; // Skip non-TS/JS files

            const filePath = file.name;
            const normalizedPath = normalizePathForMonaco(filePath);
            
            // Register with normalized path (matches bundler)
            if (!this.registeredFiles.has(`file:///${normalizedPath}`)) {
                registerWithServices(filePath, file.content);
                registeredCount.total++;
                registeredCount[ext]++;

                // Also register ambient declarations for each module
                // This enables imports with relative paths
                // Use normalized path for module declarations to match bundler
                this.registerModuleDeclaration({...file, name: normalizedPath});
            }
        });

        console.log(`[DEBUG] Registered ${registeredCount.total} files: ` +
            `${registeredCount.ts} .ts, ${registeredCount.tsx} .tsx, ` +
            `${registeredCount.js} .js, ${registeredCount.jsx} .jsx`);

        // Add React declaration to ensure JSX works correctly
        const reactDeclaration = `
    declare module 'react' {
        export const useState: any;
        export const useEffect: any;
        export const useContext: any;
        export const createContext: any;
        export const useRef: any;
        export const useCallback: any;
        export const useMemo: any;
        export const Fragment: any;
        export default any;
    }`;

        typescript.typescriptDefaults.addExtraLib(reactDeclaration, 'file:///node_modules/@types/react/index.d.ts');
    }

    private registerModuleDeclaration(file: { name: string; content: string }) {
        if (!this.monaco.languages.typescript) return;

        const typescript = this.monaco.languages.typescript;

        // Extract module name (filename without extension)
        const fileName = file.name.split('/').pop() || '';
        const moduleName = fileName.replace(/\.(ts|tsx|js|jsx)$/, '');

        // Create both relative import declarations
        const moduleDeclaration = `
    // Declaration for module ${moduleName}
    declare module './${moduleName}' {
        // Using any to allow flexible imports
        const ${moduleName}: any;
        export * from './${moduleName}';
        export default ${moduleName};
    }
    
    declare module '../${moduleName}' {
        // Using any to allow flexible imports
        const ${moduleName}: any;
        export * from '../${moduleName}';
        export default ${moduleName};
    }`;

        // Add the declaration to make sure imports work properly
        typescript.typescriptDefaults.addExtraLib(
            moduleDeclaration,
            `file:///node_modules/.virtual/${moduleName}.d.ts`
        );

        console.log(`[DEBUG] Added ambient declarations for module: ${moduleName}`);
    }

    private async handleTypeScriptDiagnostics(model: Monaco.editor.ITextModel) {
        // Clear existing markers with a consistent owner
        this.clearMarkers(model, MARKER_OWNERS.TYPESCRIPT);

        try {
            // First ensure TypeScript is available
            await this.ensureTypeScriptWorker();

            const worker = await this.getWorker('typescript');
            if (!worker) {
                console.error('[DEBUG] Failed to get TypeScript worker');
                return;
            }

            // Check if the worker is a TypeScriptWorkerWrapper
            if (!this.isTypeScriptWorkerWrapper(worker)) {
                console.error('[DEBUG] Worker is not a TypeScriptWorkerWrapper:', typeof worker);
                return;
            }

            // Safely access TypeScript worker methods
            let syntactic: any[] = [];
            let semantic: any[] = [];

            try {
                // Direct access to diagnostic methods without calling worker as a function
                syntactic = await worker.getSyntacticDiagnostics(model.uri.toString());
                semantic = await worker.getSemanticDiagnostics(model.uri.toString());
            } catch (diagError) {
                console.error('[ERROR] Failed to get diagnostics:', diagError);
                return;
            }

            console.log(`[DEBUG] Found ${syntactic.length} syntactic and ${semantic.length} semantic diagnostics`);

            if (semantic.length > 0) {
                console.log('[DEBUG] Semantic diagnostic details:');
                semantic.forEach((d, i) => {
                    const msgText = typeof d.messageText === 'string'
                        ? d.messageText
                        : d.messageText.messageText;

                    console.log(`[${i}] Code ${d.code}: ${msgText}`);

                    // If it's a module not found error, check if the file exists in our project files
                    if (d.code === 2307 && msgText.includes("Cannot find module")) {
                        const match = msgText.match(/Cannot find module ['"]([^'"]+)['"]/);
                        if (match && match[1]) {
                            const modulePath = match[1];

                            // Check if this module exists by name in our files list
                            this.checkIfModuleExists(modulePath);
                        }
                    }
                });
            }

            // Combine and filter diagnostics
            const allDiagnostics = [...syntactic, ...semantic];

            // Convert to markers, filtering out false positives for module resolution
            const markers = allDiagnostics
                .filter(d => {
                    // Filter out "Cannot find module" errors for local components that we know exist
                    if (d.code === 2307) {
                        const msgText = typeof d.messageText === 'string'
                            ? d.messageText
                            : d.messageText.messageText;

                        // Extract the module path from the error message
                        const match = msgText.match(/Cannot find module ['"]([^'"]+)['"]/);
                        if (match && match[1]) {
                            const modulePath = match[1];

                            // Check if any file in our project has this base name
                            // This handles cases like './useCanister' where the file exists but TS can't find it
                            if (this.moduleExistsInProject(modulePath)) {
                                console.log(`[DEBUG] Filtering out false positive module error for: ${modulePath}`);
                                return false;
                            }
                        }
                    }

                    // Filter out 2347 (Untyped function calls may not have type arguments)
                    if (d.code === 2347) {
                        console.log(`[DEBUG] Filtering out "Untyped function calls" error`);
                        return false;
                    }

                    return true;
                })
                .map(d => {
                    // Ensure start/end positions exist
                    const start = d.start || 0;
                    const length = d.length || 1;

                    // Get line/column positions from model
                    const startPos = model.getPositionAt(start);
                    const endPos = model.getPositionAt(start + length);

                    // Get the message text (handle both string and MessageChain formats)
                    const message = typeof d.messageText === 'string'
                        ? d.messageText
                        : this.flattenMessageChain(d.messageText);

                    // Downgrade certain errors to warnings rather than filtering them out completely
                    let severity = this.translateDiagnosticCategory(d.category);

                    // Downgrade specific error codes to warnings instead of errors
                    if (d.code === 2307) { // Cannot find module
                        severity = this.monaco.MarkerSeverity.Warning;
                    }

                    return {
                        severity,
                        startLineNumber: startPos.lineNumber,
                        startColumn: startPos.column,
                        endLineNumber: endPos.lineNumber,
                        endColumn: endPos.column,
                        message,
                        code: d.code?.toString() || '',
                        source: 'typescript'
                    };
                });

            console.log(`[DEBUG] Created ${markers.length} markers from diagnostics after filtering`);

            // Set markers with the correct owner
            this.monaco.editor.setModelMarkers(model, MARKER_OWNERS.TYPESCRIPT, markers);
        } catch (error) {
            console.error('[ERROR] Error in TypeScript diagnostics:', error);
        }
    }

    private moduleExistsInProject(importPath: string): boolean {
        // Remove ./ or ../ prefixes
        const normalizedPath = importPath.replace(/^\.\/|^\.\.\//, '');

        // Check for exact file path match
        const exactMatch = this.files.some(file => file.name === importPath);
        if (exactMatch) return true;

        // Check for files with this base name (with various extensions)
        const possibleExts = ['.ts', '.tsx', '.js', '.jsx'];

        for (const file of this.files) {
            // Extract base name from file path
            const basename = file.name.split('/').pop() || '';
            const noExt = basename.replace(/\.(ts|tsx|js|jsx)$/, '');

            // Check if it matches our import
            if (noExt === normalizedPath) {
                console.log(`[DEBUG] Found matching file for ${importPath}: ${file.name}`);
                return true;
            }

            // Also check with extensions added
            if (possibleExts.some(ext => `${normalizedPath}${ext}` === basename)) {
                console.log(`[DEBUG] Found matching file for ${importPath} with extension: ${file.name}`);
                return true;
            }
        }

        // If we're here, no matching file was found
        console.log(`[DEBUG] No matching file found for ${importPath}`);
        return false;
    }

    private checkIfModuleExists(modulePath: string): void {
        const normalizedPath = modulePath.replace(/^\.\/|^\.\.\//, '');

        // Look for matching files by name
        const matchingFiles = this.files
            .filter(f => {
                const fileName = f.name.split('/').pop() || '';
                const fileNameNoExt = fileName.replace(/\.(ts|tsx|js|jsx)$/, '');
                return fileNameNoExt === normalizedPath;
            })
            .map(f => f.name);

        console.log(`[DEBUG] Module '${modulePath}' - Found matches: ${matchingFiles.join(', ') || 'NONE'}`);
    }

    private translateDiagnosticCategory(category?: number): Monaco.MarkerSeverity {
        if (this.currentDiagnosticCode === 2347) {
            return this.monaco.MarkerSeverity.Hint; // Use Hint to effectively hide it
        }

        if (category === undefined) return this.monaco.MarkerSeverity.Warning; // Changed from Error to Warning

        // TypeScript DiagnosticCategory: 0 = Error, 1 = Warning, 2 = Suggestion, 3 = Message
        switch (category) {
            case 0:
                return this.monaco.MarkerSeverity.Error;
            case 1:
                return this.monaco.MarkerSeverity.Warning;
            case 2:
                return this.monaco.MarkerSeverity.Hint;
            case 3:
                return this.monaco.MarkerSeverity.Info;
            default:
                return this.monaco.MarkerSeverity.Warning; // Changed from Error to Warning
        }
    }

    private flattenMessageChain(messageChain: any): string {
        if (typeof messageChain === 'string') return messageChain;

        let message = messageChain.messageText || '';

        if (messageChain.next) {
            for (const next of messageChain.next) {
                message += `\n${this.flattenMessageChain(next)}`;
            }
        }

        return message;
    }

    private async handleJavaScriptDiagnostics(model: Monaco.editor.ITextModel) {
        try {
            // Specify that we want a JavaScript worker specifically
            const worker = await this.getWorker('javascript');
            if (!worker) return;

            let diagnostics: any[] = [];

            // Type guard to narrow the worker type
            if (this.isTypeScriptWorkerWrapper(worker)) {
                // Now TypeScript knows this is a TypeScriptWorkerWrapper
                if (typeof worker.getSyntacticDiagnostics === 'function') {
                    // Direct method access pattern
                    diagnostics = await worker.getSyntacticDiagnostics(model.uri.toString());
                } else if (typeof worker === 'function') {
                    // Callable function pattern
                    const client = await worker(model.uri);
                    diagnostics = await client.getSyntacticDiagnostics(model.uri.toString());
                } else {
                    console.error('JavaScript worker has unexpected structure:', worker);
                    return;
                }
            } else {
                console.error('Expected TypeScriptWorkerWrapper but got a different worker type');
                return;
            }

            const markers = diagnostics.map(d => ({
                severity: this.monaco.MarkerSeverity.Error,
                startLineNumber: model.getPositionAt(d.start!).lineNumber,
                startColumn: model.getPositionAt(d.start!).column,
                endLineNumber: model.getPositionAt(d.start! + d.length!).lineNumber,
                endColumn: model.getPositionAt(d.start! + d.length!).column,
                message: typeof d.messageText === 'string' ? d.messageText : d.messageText.messageText,
                source: 'javascript'
            }));

            this.setErrorMarkers(model, markers);
        } catch (error) {
            console.error('Error in handleJavaScriptDiagnostics:', error);
        }
    }

    private isTypeScriptWorkerWrapper(worker: any): worker is TypeScriptWorkerWrapper {
        if (!worker) return false;

        // Check if it has the methods directly
        if (typeof worker === 'object' &&
            typeof worker.getSyntacticDiagnostics === 'function' &&
            typeof worker.getSemanticDiagnostics === 'function') {
            return true;
        }

        // Check if it's callable
        if (typeof worker === 'function') {
            // We can't easily check the return value structure without calling it,
            // so we'll assume it's a worker if it's a function
            return true;
        }

        return false;
    }

    private async handleCSSDiagnostics(model: Monaco.editor.ITextModel) {
        try {
            const worker = await this.getWorker('css') as CSSWorker;
            if (!worker) return;

            const diagnostics = await worker.doValidation(model.uri.toString());

            const markers = diagnostics.map(d => ({
                severity: this.translateSeverity(d.severity),
                startLineNumber: d.range.startLineNumber,
                startColumn: d.range.startColumn,
                endLineNumber: d.range.endLineNumber,
                endColumn: d.range.endColumn,
                message: d.message,
                source: 'css'
            }));

            this.setErrorMarkers(model, markers);
        } catch (error) {
            console.error('Error in handleCSSDiagnostics:', error);
        }
    }

    private async handleHTMLDiagnostics(model: Monaco.editor.ITextModel) {
        try {
            const worker = await this.getWorker('html') as HTMLWorker;
            if (!worker) return;

            const diagnostics = await worker.doValidation(model.uri.toString());

            const markers = diagnostics.map(d => ({
                severity: this.translateSeverity(d.severity),
                startLineNumber: d.range.startLineNumber,
                startColumn: d.range.startColumn,
                endLineNumber: d.range.endLineNumber,
                endColumn: d.range.endColumn,
                message: d.message,
                source: 'html'
            }));

            this.setErrorMarkers(model, markers);
        } catch (error) {
            console.error('Error in handleHTMLDiagnostics:', error);
        }
    }

    private clearMarkers(model: Monaco.editor.ITextModel, languageId: string) {
        console.log(`[DEBUG] Clearing markers for ${languageId} on ${model.uri.toString()}`);

        // Clear specific marker owner
        this.monaco.editor.setModelMarkers(model, languageId, []);

        if (languageId === MARKER_OWNERS.MOTOKO) {
            // Also clear legacy Motoko markers
            const legacyMotokoOwners = ['motoko', 'motoko-import', 'motoko-test', 'motoko-force-test'];
            legacyMotokoOwners.forEach(owner => {
                this.monaco.editor.setModelMarkers(model, owner, []);
            });
            this.monaco.editor.setModelMarkers(model, 'motoko-custom-diagnostics', []);
        } else if (languageId === MARKER_OWNERS.TYPESCRIPT) {
            // Clear all possible TypeScript-related markers
            this.monaco.editor.setModelMarkers(model, 'typescript', []);
            this.monaco.editor.setModelMarkers(model, 'ts', []);
            this.monaco.editor.setModelMarkers(model, 'typescript-language-features', []);
        }

        // Always clear general diagnostics too
        this.monaco.editor.setModelMarkers(model, MARKER_OWNERS.GENERAL, []);

        // Verify markers were cleared
        setTimeout(() => {
            try {
                const currentMarkers = this.monaco.editor.getModelMarkers({
                    resource: model.uri,
                    owner: languageId
                });
                console.log(`[DEBUG] After clearing, ${currentMarkers.length} markers remain with owner ${languageId}`);
                if (currentMarkers.length > 0) {
                    console.warn('[WARNING] Failed to clear all markers!');
                }
            } catch (e) {
                console.error('[ERROR] Error verifying marker clearing:', e);
            }
        }, 10);
    }

    public forceCleanAllMarkers(model: Monaco.editor.ITextModel) {
        if (!model) return;

        console.log('[MARKER CLEANER] Forcibly removing ALL markers from', model.uri.toString());

        // Use a comprehensive list of known owners, including our defined constants
        const owners = [
            // Our standardized owner constants
            ...Object.values(MARKER_OWNERS),

            // Legacy/additional Motoko-related owners
            'motoko', 'motoko-import', 'motoko-test', 'motoko-force-test', 'motoko-custom-diagnostics',

            // Legacy/additional TypeScript/JavaScript-related owners
            'typescript', 'ts', 'typescript-language-features',
            'javascript', 'js',

            // Other language owners
            'html', 'css', 'json',

            // Generic/misc owners
            'monaco-editor', 'default', 'monaco', 'languageId', 'editor'
        ];

        console.log(`[MARKER CLEANER] Clearing markers for ${owners.length} owners`);

        // Clear markers for all known owners
        owners.forEach(owner => {
            this.monaco.editor.setModelMarkers(model, owner, []);
        });

        // Verify markers were cleared
        setTimeout(() => {
            try {
                const currentMarkers = this.monaco.editor.getModelMarkers({resource: model.uri});
                console.log(`[MARKER CLEANER] After forceful cleaning, ${currentMarkers.length} markers remain`);
                if (currentMarkers.length > 0) {
                    console.warn('[WARNING] Failed to clear all markers:',
                        currentMarkers.map(m => `${m.owner || 'unknown'}:${m.message}`));
                }
            } catch (e) {
                console.error('[ERROR] Error verifying marker cleaning:', e);
            }
        }, 20);

        console.log('[MARKER CLEANER] Completed marker clearing operation');
    }

    private setErrorMarkers(model: Monaco.editor.ITextModel, markers: Monaco.editor.IMarkerData[]) {
        // Set owner based on file type
        const extension = model.uri.path.split('.').pop()?.toLowerCase();
        let owner = MARKER_OWNERS.GENERAL;

        if (extension === 'mo') {
            owner = MARKER_OWNERS.MOTOKO;
        } else if (extension === 'ts' || extension === 'tsx') {
            owner = MARKER_OWNERS.TYPESCRIPT;
        } else if (extension === 'js' || extension === 'jsx') {
            owner = MARKER_OWNERS.JAVASCRIPT;
        } else if (extension === 'css') {
            owner = MARKER_OWNERS.CSS;
        } else if (extension === 'html') {
            owner = MARKER_OWNERS.HTML;
        }

        // Clear any existing markers for this owner
        this.clearMarkers(model, owner);

        // Then set the new markers
        if (markers.length > 0) {
            this.monaco.editor.setModelMarkers(model, owner, markers);
        }
    }

    private translateSeverity(severity: number): Monaco.MarkerSeverity {
        switch (severity) {
            case 1:
                return this.monaco.MarkerSeverity.Error;
            case 2:
                return this.monaco.MarkerSeverity.Warning;
            case 3:
                return this.monaco.MarkerSeverity.Info;
            case 4:
                return this.monaco.MarkerSeverity.Hint;
            default:
                return this.monaco.MarkerSeverity.Info;
        }
    }

    public dispose(): void {
        if (this.diagnosticsTimeout) {
            clearTimeout(this.diagnosticsTimeout);
        }
        this.disposed = true;
        Object.values(this.workers).forEach(worker => {
            if (worker && typeof (worker as any).dispose === 'function') {
                (worker as any).dispose();
            }
        });
        this.workers = {};
    }
}
